import { useAppSelector } from '@/app/redux/hooks';
import { selectSessionUserDetails } from '@/app/redux/slices/sessionSlice';
import {
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import { Skeleton } from '@shadcn/components/skeleton';
import { useEffect, useRef, useState } from 'react';

const USERJOT_PROJECT_ID = 'cmlq57eq701sl0inzuggbfvtd';

/**
 * Embeds the UserJot feedback board as a full-page iframe within this component
 */
export default function FeatureRequestsView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const embedRef = useRef<ReturnType<typeof window.uj.embed> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sdkReady, setSdkReady] = useState(false);
  const userJotInitialised = useRef(false);
  const userDetails = useAppSelector(selectSessionUserDetails);

  // Initialise the UserJot SDK once
  useEffect(() => {
    if (userJotInitialised.current) return;
    userJotInitialised.current = true;
    window.uj.init(USERJOT_PROJECT_ID, {
      widget: false,
      theme: 'light',
      onReady: () => setSdkReady(true),
    });
  }, []);

  // Identify / un-identify the user when auth state changes
  useEffect(() => {
    if (!sdkReady) return;
    if (userDetails) {
      window.uj.identify({ id: userDetails.uid, email: userDetails.email });
    } else {
      window.uj.identify(null);
    }
  }, [userDetails, sdkReady]);

  // Create embed only after SDK is ready
  useEffect(() => {
    if (!sdkReady || !containerRef.current || embedRef.current) return;

    embedRef.current = window.uj.embed({
      container: '#userjot-embed',
      path: '/',
      theme: 'light',
      navbar: 'hidden',
      onReady: () => setIsLoading(false),
    });

    return () => {
      embedRef.current?.destroy();
      embedRef.current = null;
    };
  }, [sdkReady]);

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>Feature Requests</DashboardViewTitle>
      </DashboardViewHeader>
      <DashboardViewContent>
        {isLoading && <EmbedSkeleton />}
        <div
          id="userjot-embed"
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            background: '#ffffff',
            display: isLoading ? 'none' : 'block',
          }}
        />
      </DashboardViewContent>
    </DashboardViewContainer>
  );
}

function EmbedSkeleton() {
  return (
    <div
      className="flex flex-col gap-4 p-4"
      style={{ height: '100%' }}
    >
      <div className="flex gap-3">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-32" />
      </div>
      <Skeleton className="h-10 w-full" />
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-lg border border-gray-200 p-4"
        >
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  );
}
